import { Answer } from "@domain/Answer";
import { AnswerFactory } from "@domain/factories/Answer.factory";
import { shuffleArray } from "@helpers/array.helper";
import { normalizeString } from "@helpers/string.helper";

export class ExerciseId {
  readonly value: string;

  constructor(id: string) {
    this.value = id;
  }

  toString(): string {
    return this.value;
  }

  isEqualTo(exerciseId: ExerciseId) {
    return this.value === exerciseId.toString();
  }
}

export interface ExerciseProps {
  id: ExerciseId;
  sentence: string;
  correctAnswer: string;
  positiveFeedback: string;
  constructiveFeedback: string;
}

export abstract class Exercise {
  readonly id: ExerciseId;
  readonly sentence: string;
  readonly correctAnswer: string;
  readonly positiveFeedback: string;
  readonly constructiveFeedback: string;

  constructor(props: ExerciseProps) {
    this.id = props.id;
    this.sentence = props.sentence;
    this.correctAnswer = props.correctAnswer;
    this.positiveFeedback = props.positiveFeedback;
    this.constructiveFeedback = props.constructiveFeedback;
  }

  abstract getSentenceToCompleteParts(): string[];
  abstract submitAnswer(_: Answer): Answer;
  abstract getCompletedSentence(_answer: Answer): string;
  abstract getCorrectSentence(): string;
}

export type AudioExerciseProps = ExerciseProps & {
  readonly audioFileName: string;
};

export class AudioExercise extends Exercise {
  audioFileName: string;

  constructor(props: AudioExerciseProps) {
    const { id, sentence, correctAnswer, positiveFeedback, constructiveFeedback } = props;
    super({ id, sentence, correctAnswer, positiveFeedback, constructiveFeedback });

    this.audioFileName = props.audioFileName;
  }

  getSentenceToCompleteParts() {
    return this.sentence
      .trim()
      .split(/(\[textfield\])/)
      .filter(part => part !== "");
  }

  submitAnswer(answer: Answer) {
    const normalizedAnswerValue = normalizeString(answer.value.trim().toLowerCase());
    const normalizedCorrectAnswer = normalizeString(this.correctAnswer.trim().toLowerCase());

    const isAnswerCorrect = normalizedAnswerValue === normalizedCorrectAnswer;
    return AnswerFactory.createSubmittedAnswer(answer, isAnswerCorrect);
  }

  getCompletedSentence(answer: Answer): string {
    return this.sentence.replace("[textfield]", answer.value);
  }

  getCorrectSentence(): string {
    return this.sentence.replace("[textfield]", this.correctAnswer);
  }
}

export type DropDownExerciseProps = ExerciseProps & {
  suggestedAnswers: string[];
};

export class DropDownExercise extends Exercise {
  readonly suggestedAnswers: string[];

  constructor(props: DropDownExerciseProps) {
    const { id, sentence, correctAnswer, positiveFeedback, constructiveFeedback } = props;
    super({ id, sentence, correctAnswer, positiveFeedback, constructiveFeedback });

    this.suggestedAnswers = shuffleArray<string>(props.suggestedAnswers);
  }

  getSentenceToCompleteParts() {
    return this.sentence.split(/(\[dropdown\])/);
  }

  submitAnswer(answer: Answer) {
    const isAnswerCorrect = answer.value === this.correctAnswer;
    return AnswerFactory.createSubmittedAnswer(answer, isAnswerCorrect);
  }

  getCompletedSentence(answer: Answer): string {
    return this.sentence.replace("[dropdown]", answer.value);
  }

  getCorrectSentence(): string {
    return this.sentence.replace("[dropdown]", this.correctAnswer);
  }
}
